import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Building2,
  Globe,
  Users,
  Package,
  Calendar,
  Loader2,
  RefreshCw,
  Palette,
  Image,
  Settings,
  CreditCard,
  Clock,
  DollarSign,
  FileText,
  Shield,
  Ticket,
  UserCheck,
  ChevronRight,
  Edit,
  X,
  Save,
  Upload,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn, formatDate } from '@/lib/utils';
import { useAuthStore } from '@/stores/authStore';
import api from '@/services/api';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/useToast';

interface Organization {
  id: string;
  name: string;
  slug: string;
  domain?: string;
  
  // Subscription
  subscriptionPlan?: string;
  maxUsers?: number;
  maxAssets?: number;
  
  // Contact Information
  address?: string;
  city?: string;
  country?: string;
  phone?: string;
  email?: string;
  website?: string;
  registrationNumber?: string;
  taxId?: string;
  
  // Branding
  logo?: string;
  logoDark?: string;
  favicon?: string;
  primaryColor?: string;
  secondaryColor?: string;
  accentColor?: string;
  
  // Letterhead & PDF
  letterheadEnabled?: boolean;
  letterheadImage?: string;
  letterheadHeight?: number;
  footerText?: string;
  tagline?: string;
  watermarkEnabled?: boolean;
  watermarkText?: string;
  watermarkOpacity?: number;
  
  // Login Page
  loginBackground?: string;
  loginOverlayColor?: string;
  loginOverlayOpacity?: number;
  loginWelcomeMessage?: string;
  
  // Settings
  timezone?: string;
  dateFormat?: string;
  currency?: string;
  
  isActive?: boolean;
  createdAt: string;
  updatedAt: string;
  
  _count?: {
    users: number;
    assets: number;
    branches: number;
    departments: number;
  };
}

interface OrganizationStats {
  totalUsers: number;
  totalAssets: number;
  totalBranches: number;
  totalDepartments: number;
  totalTickets: number;
  totalEmployees: number;
}

export default function OrganizationPage() {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState<'details' | 'contact' | 'branding' | 'settings'>('details');
  
  // Edit modal states
  const [showDetailsEdit, setShowDetailsEdit] = useState(false);
  const [showBrandingEdit, setShowBrandingEdit] = useState(false);
  const [showSettingsEdit, setShowSettingsEdit] = useState(false);
  const [showContactEdit, setShowContactEdit] = useState(false);
  
  // Form data for editing
  const [detailsForm, setDetailsForm] = useState({
    name: '',
    domain: '',
    tagline: '',
  });
  
  const [contactForm, setContactForm] = useState({
    address: '',
    city: '',
    country: '',
    phone: '',
    email: '',
    website: '',
    registrationNumber: '',
    taxId: '',
  });
  
  const [brandingForm, setBrandingForm] = useState({
    logo: '',
    logoDark: '',
    favicon: '',
    primaryColor: '#3B82F6',
    secondaryColor: '#1E40AF',
    accentColor: '#10B981',
    letterheadEnabled: false,
    letterheadImage: '',
    footerText: '',
    watermarkEnabled: false,
    watermarkText: '',
    watermarkOpacity: 0.1,
    loginBackground: '',
    loginOverlayColor: '',
    loginOverlayOpacity: 0.5,
    loginWelcomeMessage: '',
  });
  
  const [settingsForm, setSettingsForm] = useState({
    timezone: 'UTC',
    dateFormat: 'YYYY-MM-DD',
    currency: 'USD',
  });

  // Fetch organization data
  const { data: organization, isLoading, error, refetch } = useQuery({
    queryKey: ['organization'],
    queryFn: async () => {
      const response = await api.get('/organization');
      return response.data.organization as Organization;
    },
  });

  // Fetch organization stats
  const { data: stats } = useQuery({
    queryKey: ['organization-stats'],
    queryFn: async () => {
      const response = await api.get('/organization/stats');
      return response.data as OrganizationStats;
    },
  });
  
  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async (data: Partial<Organization>) => {
      return api.put('/organization', data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization'] });
      setShowDetailsEdit(false);
      setShowBrandingEdit(false);
      setShowSettingsEdit(false);
      toast({ title: 'Success', description: 'Organization updated successfully' });
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to update organization', variant: 'destructive' });
    },
  });

  // File upload mutation
  const uploadMutation = useMutation({
    mutationFn: async ({ file, type }: { file: File; type: string }) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', type);
      return api.post('/organization/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },
    onSuccess: (response, variables) => {
      queryClient.invalidateQueries({ queryKey: ['organization'] });
      // Update the branding form with the new file path
      setBrandingForm(prev => ({ ...prev, [variables.type]: response.data.filePath }));
      toast({ title: 'Success', description: 'File uploaded successfully' });
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to upload file', variant: 'destructive' });
    },
  });

  // Delete file mutation
  const deleteFileMutation = useMutation({
    mutationFn: async (type: string) => {
      return api.delete(`/organization/upload/${type}`);
    },
    onSuccess: (_response, type) => {
      queryClient.invalidateQueries({ queryKey: ['organization'] });
      setBrandingForm(prev => ({ ...prev, [type]: '' }));
      toast({ title: 'Success', description: 'File removed successfully' });
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to remove file', variant: 'destructive' });
    },
  });

  // Handle file input change
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>, type: string) => {
    const file = e.target.files?.[0];
    if (file) {
      uploadMutation.mutate({ file, type });
    }
  };
  
  const handleDetailsSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateMutation.mutate(detailsForm);
  };
  
  const handleBrandingSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateMutation.mutate(brandingForm);
  };
  
  const handleSettingsSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateMutation.mutate(settingsForm);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <p className="text-destructive mb-4">Failed to load organization details.</p>
          <Button onClick={() => refetch()}>Try Again</Button>
        </div>
      </div>
    );
  }

  const orgData: Organization = organization || {
    id: user?.organization?.id || '',
    name: user?.organization?.name || 'My Organization',
    slug: (user?.organization as any)?.slug || 'org',
    subscriptionPlan: 'free',
    maxUsers: 5,
    maxAssets: 100,
    primaryColor: '#3B82F6',
    secondaryColor: '#1E40AF',
    accentColor: '#10B981',
    timezone: 'UTC',
    dateFormat: 'YYYY-MM-DD',
    currency: 'USD',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
  
  // Open edit modals with current data
  const openDetailsEdit = () => {
    setDetailsForm({
      name: orgData.name || '',
      domain: orgData.domain || '',
      tagline: orgData.tagline || '',
    });
    setShowDetailsEdit(true);
  };
  
  const openBrandingEdit = () => {
    setBrandingForm({
      logo: orgData.logo || '',
      logoDark: orgData.logoDark || '',
      favicon: orgData.favicon || '',
      primaryColor: orgData.primaryColor || '#3B82F6',
      secondaryColor: orgData.secondaryColor || '#1E40AF',
      accentColor: orgData.accentColor || '#10B981',
      letterheadEnabled: orgData.letterheadEnabled || false,
      letterheadImage: orgData.letterheadImage || '',
      footerText: orgData.footerText || '',
      watermarkEnabled: orgData.watermarkEnabled || false,
      watermarkText: orgData.watermarkText || '',
      watermarkOpacity: orgData.watermarkOpacity || 0.1,
      loginBackground: orgData.loginBackground || '',
      loginOverlayColor: orgData.loginOverlayColor || '',
      loginOverlayOpacity: orgData.loginOverlayOpacity || 0.5,
      loginWelcomeMessage: orgData.loginWelcomeMessage || '',
    });
    setShowBrandingEdit(true);
  };
  
  const openSettingsEdit = () => {
    setSettingsForm({
      timezone: orgData.timezone || 'UTC',
      dateFormat: orgData.dateFormat || 'YYYY-MM-DD',
      currency: orgData.currency || 'USD',
    });
    setShowSettingsEdit(true);
  };

  // Helper to get subscription plan badge color
  const getPlanColor = (plan: string) => {
    switch (plan?.toLowerCase()) {
      case 'enterprise': return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400';
      case 'professional': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
      case 'starter': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400';
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Building2 className="w-8 h-8" />
            Organization
          </h1>
          <p className="text-muted-foreground">
            View organization details and branding settings
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => refetch()}>
          <RefreshCw className={cn('w-4 h-4 mr-2', isLoading && 'animate-spin')} />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-6">
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/users')}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <Users className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-muted-foreground">Users</p>
                <div className="flex items-center justify-between">
                  <p className="text-2xl font-bold">{stats?.totalUsers || orgData._count?.users || 0}</p>
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/employees')}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-cyan-100 dark:bg-cyan-900/30 rounded-lg">
                <UserCheck className="w-6 h-6 text-cyan-600 dark:text-cyan-400" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-muted-foreground">Employees</p>
                <div className="flex items-center justify-between">
                  <p className="text-2xl font-bold">{stats?.totalEmployees || 0}</p>
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/assets')}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-lg">
                <Package className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-muted-foreground">Assets</p>
                <div className="flex items-center justify-between">
                  <p className="text-2xl font-bold">{stats?.totalAssets || orgData._count?.assets || 0}</p>
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/branches')}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                <Building2 className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-muted-foreground">Branches</p>
                <div className="flex items-center justify-between">
                  <p className="text-2xl font-bold">{stats?.totalBranches || orgData._count?.branches || 0}</p>
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/departments')}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                <Settings className="w-6 h-6 text-orange-600 dark:text-orange-400" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-muted-foreground">Departments</p>
                <div className="flex items-center justify-between">
                  <p className="text-2xl font-bold">{stats?.totalDepartments || orgData._count?.departments || 0}</p>
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/tickets')}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-rose-100 dark:bg-rose-900/30 rounded-lg">
                <Ticket className="w-6 h-6 text-rose-600 dark:text-rose-400" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-muted-foreground">Tickets</p>
                <div className="flex items-center justify-between">
                  <p className="text-2xl font-bold">{stats?.totalTickets || 0}</p>
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tab Navigation */}
      <div className="border-b">
        <nav className="flex gap-4">
          <button
            onClick={() => setActiveTab('details')}
            className={cn(
              'px-4 py-2 text-sm font-medium border-b-2 transition-colors',
              activeTab === 'details'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            )}
          >
            <Building2 className="w-4 h-4 inline mr-2" />
            Details
          </button>
          <button
            onClick={() => setActiveTab('contact')}
            className={cn(
              'px-4 py-2 text-sm font-medium border-b-2 transition-colors',
              activeTab === 'contact'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            )}
          >
            <Globe className="w-4 h-4 inline mr-2" />
            Contact Info
          </button>
          <button
            onClick={() => setActiveTab('branding')}
            className={cn(
              'px-4 py-2 text-sm font-medium border-b-2 transition-colors',
              activeTab === 'branding'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            )}
          >
            <Palette className="w-4 h-4 inline mr-2" />
            Branding
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className={cn(
              'px-4 py-2 text-sm font-medium border-b-2 transition-colors',
              activeTab === 'settings'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            )}
          >
            <Settings className="w-4 h-4 inline mr-2" />
            Settings
          </button>
        </nav>
      </div>

      {/* Details Tab */}
      {activeTab === 'details' && (
        <div className="space-y-6">
          <div className="flex justify-end">
            <Button onClick={openDetailsEdit}>
              <Edit className="w-4 h-4 mr-2" />
              Edit Details
            </Button>
          </div>
          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5" />
                  Basic Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4">
                  <div>
                    <label className="text-sm text-muted-foreground">Organization Name</label>
                    <p className="font-medium text-lg">{orgData.name}</p>
                  </div>
                  <div>
                    <label className="text-sm text-muted-foreground">Slug / URL Identifier</label>
                    <p className="font-medium font-mono bg-muted px-2 py-1 rounded w-fit">{orgData.slug}</p>
                  </div>
                  {orgData.domain && (
                    <div className="flex items-start gap-2">
                      <Globe className="w-4 h-4 text-muted-foreground mt-1" />
                      <div>
                        <label className="text-sm text-muted-foreground">Domain</label>
                        <p className="font-medium">{orgData.domain}</p>
                      </div>
                    </div>
                  )}
                  {orgData.tagline && (
                    <div>
                      <label className="text-sm text-muted-foreground">Tagline</label>
                    <p className="font-medium italic">"{orgData.tagline}"</p>
                  </div>
                )}
                <div>
                  <label className="text-sm text-muted-foreground">Status</label>
                  <p>
                    <span className={cn(
                      "px-2 py-1 rounded-full text-xs font-medium",
                      orgData.isActive !== false
                        ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                        : "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"
                    )}>
                      {orgData.isActive !== false ? 'Active' : 'Inactive'}
                    </span>
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                Subscription & Limits
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4">
                <div>
                  <label className="text-sm text-muted-foreground">Subscription Plan</label>
                  <p>
                    <span className={cn(
                      "px-3 py-1 rounded-full text-sm font-medium capitalize",
                      getPlanColor(orgData.subscriptionPlan || 'free')
                    )}>
                      {orgData.subscriptionPlan || 'Free'}
                    </span>
                  </p>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground">User Limit</label>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-blue-500 rounded-full transition-all"
                        style={{ 
                          width: `${Math.min(100, ((stats?.totalUsers || 0) / (orgData.maxUsers || 5)) * 100)}%` 
                        }}
                      />
                    </div>
                    <span className="text-sm font-medium">
                      {stats?.totalUsers || 0} / {orgData.maxUsers || 5}
                    </span>
                  </div>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground">Asset Limit</label>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-green-500 rounded-full transition-all"
                        style={{ 
                          width: `${Math.min(100, ((stats?.totalAssets || 0) / (orgData.maxAssets || 100)) * 100)}%` 
                        }}
                      />
                    </div>
                    <span className="text-sm font-medium">
                      {stats?.totalAssets || 0} / {orgData.maxAssets || 100}
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="md:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Timestamps
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="text-sm text-muted-foreground">Created</label>
                  <p className="font-medium">{formatDate(orgData.createdAt)}</p>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground">Last Updated</label>
                  <p className="font-medium">{formatDate(orgData.updatedAt)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        </div>
      )}

      {/* Contact Info Tab */}
      {activeTab === 'contact' && (
        <div className="space-y-6">
          <div className="flex justify-end">
            <Button onClick={() => {
              setContactForm({
                address: organization?.address || '',
                city: organization?.city || '',
                country: organization?.country || '',
                phone: organization?.phone || '',
                email: organization?.email || '',
                website: organization?.website || '',
                registrationNumber: organization?.registrationNumber || '',
                taxId: organization?.taxId || '',
              });
              setShowContactEdit(true);
            }}>
              <Edit className="w-4 h-4 mr-2" />
              Edit Contact Info
            </Button>
          </div>
          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="w-5 h-5" />
                  Contact Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4">
                  <div>
                    <label className="text-sm text-muted-foreground">Address</label>
                    <p className="font-medium">{organization?.address || 'Not set'}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-muted-foreground">City</label>
                      <p className="font-medium">{organization?.city || 'Not set'}</p>
                    </div>
                    <div>
                      <label className="text-sm text-muted-foreground">Country</label>
                      <p className="font-medium">{organization?.country || 'Not set'}</p>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm text-muted-foreground">Phone</label>
                    <p className="font-medium">{organization?.phone || 'Not set'}</p>
                  </div>
                  <div>
                    <label className="text-sm text-muted-foreground">Email</label>
                    <p className="font-medium">{organization?.email || 'Not set'}</p>
                  </div>
                  <div>
                    <label className="text-sm text-muted-foreground">Website</label>
                    <p className="font-medium">{organization?.website || 'Not set'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Legal Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4">
                  <div>
                    <label className="text-sm text-muted-foreground">Company Registration Number</label>
                    <p className="font-medium">{organization?.registrationNumber || 'Not set'}</p>
                  </div>
                  <div>
                    <label className="text-sm text-muted-foreground">Tax ID / VAT Number</label>
                    <p className="font-medium">{organization?.taxId || 'Not set'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Contact Info Preview for Reports */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Report Letterhead Preview
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="p-6 border rounded-lg bg-white dark:bg-gray-950">
                <div className="text-center space-y-1">
                  <h2 className="text-xl font-bold" style={{ color: organization?.primaryColor || '#3B82F6' }}>
                    {organization?.name?.toUpperCase() || 'ORGANIZATION NAME'}
                  </h2>
                  {organization?.tagline && (
                    <p className="text-sm text-muted-foreground italic">{organization.tagline}</p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    {[
                      organization?.address,
                      organization?.city,
                      organization?.country,
                    ].filter(Boolean).join(', ') || 'Address not set'}
                    {organization?.phone && ` | Tel: ${organization.phone}`}
                    {organization?.email && ` | ${organization.email}`}
                  </p>
                </div>
                <hr className="my-4" style={{ borderColor: organization?.primaryColor || '#3B82F6' }} />
                <p className="text-center text-sm text-muted-foreground">
                  This is how your organization header will appear on exported reports
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Contact Edit Modal */}
      {showContactEdit && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-auto">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Edit Contact Information</CardTitle>
              <Button variant="ghost" size="icon" onClick={() => setShowContactEdit(false)}>
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Address</Label>
                <Input
                  value={contactForm.address}
                  onChange={(e) => setContactForm({ ...contactForm, address: e.target.value })}
                  placeholder="Street address"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>City</Label>
                  <Input
                    value={contactForm.city}
                    onChange={(e) => setContactForm({ ...contactForm, city: e.target.value })}
                    placeholder="City"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Country</Label>
                  <Input
                    value={contactForm.country}
                    onChange={(e) => setContactForm({ ...contactForm, country: e.target.value })}
                    placeholder="Country"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input
                    value={contactForm.phone}
                    onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                    placeholder="+256 xxx xxx xxx"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={contactForm.email}
                    onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                    placeholder="info@company.com"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Website</Label>
                <Input
                  value={contactForm.website}
                  onChange={(e) => setContactForm({ ...contactForm, website: e.target.value })}
                  placeholder="https://www.company.com"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Registration Number</Label>
                  <Input
                    value={contactForm.registrationNumber}
                    onChange={(e) => setContactForm({ ...contactForm, registrationNumber: e.target.value })}
                    placeholder="Company registration number"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Tax ID / VAT Number</Label>
                  <Input
                    value={contactForm.taxId}
                    onChange={(e) => setContactForm({ ...contactForm, taxId: e.target.value })}
                    placeholder="Tax ID or VAT number"
                  />
                </div>
              </div>
              <div className="flex gap-2 justify-end pt-4">
                <Button variant="outline" onClick={() => setShowContactEdit(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={async () => {
                    try {
                      await api.patch('/organization', contactForm);
                      toast({ title: 'Success', description: 'Contact information updated' });
                      setShowContactEdit(false);
                      refetch();
                    } catch (error) {
                      toast({ title: 'Error', description: 'Failed to update contact info', variant: 'destructive' });
                    }
                  }}
                >
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Branding Tab */}
      {activeTab === 'branding' && (
        <div className="space-y-6">
          <div className="flex justify-end">
            <Button onClick={openBrandingEdit}>
              <Edit className="w-4 h-4 mr-2" />
              Edit Branding
            </Button>
          </div>
          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Image className="w-5 h-5" />
                  Logos
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm text-muted-foreground mb-2 block">Primary Logo</label>
                  <div className="flex items-center justify-center p-6 border-2 border-dashed rounded-lg bg-muted/50">
                  {orgData.logo ? (
                    <img src={orgData.logo} alt="Organization Logo" className="max-h-24 object-contain" />
                  ) : (
                    <div className="text-center">
                      <Building2 className="w-12 h-12 mx-auto text-muted-foreground" />
                      <p className="mt-2 text-sm text-muted-foreground">No logo uploaded</p>
                    </div>
                  )}
                </div>
              </div>
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">Dark Mode Logo</label>
                <div className="flex items-center justify-center p-6 border-2 border-dashed rounded-lg bg-gray-800">
                  {orgData.logoDark ? (
                    <img src={orgData.logoDark} alt="Dark Mode Logo" className="max-h-24 object-contain" />
                  ) : orgData.logo ? (
                    <img src={orgData.logo} alt="Organization Logo" className="max-h-24 object-contain" />
                  ) : (
                    <div className="text-center">
                      <Building2 className="w-12 h-12 mx-auto text-gray-500" />
                      <p className="mt-2 text-sm text-gray-500">No dark logo</p>
                    </div>
                  )}
                </div>
              </div>
              {orgData.favicon && (
                <div>
                  <label className="text-sm text-muted-foreground mb-2 block">Favicon</label>
                  <div className="flex items-center gap-3">
                    <img src={orgData.favicon} alt="Favicon" className="w-8 h-8 object-contain" />
                    <span className="text-sm text-muted-foreground">Browser tab icon</span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Palette className="w-5 h-5" />
                Brand Colors
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm text-muted-foreground">Primary Color</label>
                <div className="flex items-center gap-3 mt-1">
                  <div
                    className="w-10 h-10 rounded-lg border shadow-sm"
                    style={{ backgroundColor: orgData.primaryColor || '#3B82F6' }}
                  />
                  <span className="font-mono">{orgData.primaryColor || '#3B82F6'}</span>
                </div>
              </div>
              <div>
                <label className="text-sm text-muted-foreground">Secondary Color</label>
                <div className="flex items-center gap-3 mt-1">
                  <div
                    className="w-10 h-10 rounded-lg border shadow-sm"
                    style={{ backgroundColor: orgData.secondaryColor || '#1E40AF' }}
                  />
                  <span className="font-mono">{orgData.secondaryColor || '#1E40AF'}</span>
                </div>
              </div>
              <div>
                <label className="text-sm text-muted-foreground">Accent Color</label>
                <div className="flex items-center gap-3 mt-1">
                  <div
                    className="w-10 h-10 rounded-lg border shadow-sm"
                    style={{ backgroundColor: orgData.accentColor || '#10B981' }}
                  />
                  <span className="font-mono">{orgData.accentColor || '#10B981'}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Letterhead & PDF Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm">Letterhead Enabled</span>
                <span className={cn(
                  "px-2 py-1 rounded-full text-xs font-medium",
                  orgData.letterheadEnabled
                    ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                    : "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400"
                )}>
                  {orgData.letterheadEnabled ? 'Yes' : 'No'}
                </span>
              </div>
              {orgData.letterheadImage && (
                <div>
                  <label className="text-sm text-muted-foreground mb-2 block">Letterhead Image</label>
                  <div className="border rounded-lg p-2">
                    <img src={orgData.letterheadImage} alt="Letterhead" className="w-full h-auto max-h-24 object-contain" />
                  </div>
                </div>
              )}
              {orgData.footerText && (
                <div>
                  <label className="text-sm text-muted-foreground">Footer Text</label>
                  <p className="text-sm mt-1">{orgData.footerText}</p>
                </div>
              )}
              <div className="flex items-center justify-between">
                <span className="text-sm">Watermark Enabled</span>
                <span className={cn(
                  "px-2 py-1 rounded-full text-xs font-medium",
                  orgData.watermarkEnabled
                    ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                    : "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400"
                )}>
                  {orgData.watermarkEnabled ? 'Yes' : 'No'}
                </span>
              </div>
              {orgData.watermarkText && (
                <div>
                  <label className="text-sm text-muted-foreground">Watermark Text</label>
                  <p className="text-sm mt-1 opacity-50">{orgData.watermarkText}</p>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5" />
                Login Page Customization
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {orgData.loginWelcomeMessage && (
                <div>
                  <label className="text-sm text-muted-foreground">Welcome Message</label>
                  <p className="text-sm mt-1 font-medium">{orgData.loginWelcomeMessage}</p>
                </div>
              )}
              {orgData.loginBackground && (
                <div>
                  <label className="text-sm text-muted-foreground mb-2 block">Login Background</label>
                  <div className="relative border rounded-lg overflow-hidden h-24">
                    <img src={orgData.loginBackground} alt="Login Background" className="w-full h-full object-cover" />
                    {orgData.loginOverlayColor && (
                      <div 
                        className="absolute inset-0"
                        style={{ 
                          backgroundColor: orgData.loginOverlayColor,
                          opacity: orgData.loginOverlayOpacity || 0.5
                        }}
                      />
                    )}
                  </div>
                </div>
              )}
              {!orgData.loginWelcomeMessage && !orgData.loginBackground && (
                <p className="text-sm text-muted-foreground">No login page customization configured</p>
              )}
              
              {/* Preview Login Page Button */}
              <div className="pt-4 border-t">
                <Button 
                  variant="outline" 
                  onClick={() => window.open('/login', '_blank')}
                  className="w-full sm:w-auto"
                >
                  <Globe className="w-4 h-4 mr-2" />
                  Preview Login Page
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className="md:col-span-2">
            <CardHeader>
              <CardTitle>Preview</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="p-4 border rounded-lg">
                <div className="flex items-center gap-3 pb-4 border-b">
                  {orgData.logo ? (
                    <img src={orgData.logo} alt="Logo" className="h-10 object-contain" />
                  ) : (
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: orgData.primaryColor || '#3B82F6' }}
                    >
                      <Package className="w-6 h-6 text-white" />
                    </div>
                  )}
                  <span className="font-bold text-lg">{orgData.name}</span>
                </div>
                <div className="pt-4 flex flex-wrap gap-2">
                  <button
                    className="px-4 py-2 rounded-lg text-white text-sm font-medium"
                    style={{ backgroundColor: orgData.primaryColor || '#3B82F6' }}
                  >
                    Primary Button
                  </button>
                  <button
                    className="px-4 py-2 rounded-lg text-white text-sm font-medium"
                    style={{ backgroundColor: orgData.secondaryColor || '#1E40AF' }}
                  >
                    Secondary Button
                  </button>
                  <button
                    className="px-4 py-2 rounded-lg text-white text-sm font-medium"
                    style={{ backgroundColor: orgData.accentColor || '#10B981' }}
                  >
                    Accent Button
                  </button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        </div>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <div className="space-y-6">
          <div className="flex justify-end">
            <Button onClick={openSettingsEdit}>
              <Edit className="w-4 h-4 mr-2" />
              Edit Settings
            </Button>
          </div>
          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  Regional Settings
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Sync notice */}
                <div className="p-3 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <p className="text-xs text-blue-700 dark:text-blue-300">
                    These settings sync with the General Settings page for consistent formatting across the system.
                  </p>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground">Timezone</label>
                  <p className="font-medium">{orgData.timezone || 'UTC'}</p>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground">Date Format</label>
                  <p className="font-medium font-mono">{orgData.dateFormat || 'YYYY-MM-DD'}</p>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground">Currency</label>
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-muted-foreground" />
                    <p className="font-medium">{orgData.currency || 'USD'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                Plan Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm text-muted-foreground">Current Plan</label>
                <p>
                  <span className={cn(
                    "px-3 py-1 rounded-full text-sm font-medium capitalize",
                    getPlanColor(orgData.subscriptionPlan || 'free')
                  )}>
                    {orgData.subscriptionPlan || 'Free'}
                  </span>
                </p>
              </div>
              <div>
                <label className="text-sm text-muted-foreground">Maximum Users Allowed</label>
                <p className="font-medium text-lg">{orgData.maxUsers || 5}</p>
              </div>
              <div>
                <label className="text-sm text-muted-foreground">Maximum Assets Allowed</label>
                <p className="font-medium text-lg">{orgData.maxAssets || 100}</p>
              </div>
            </CardContent>
          </Card>

          <Card className="md:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Usage Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-6 md:grid-cols-4">
                <div className="text-center p-4 bg-muted/50 rounded-lg">
                  <Users className="w-8 h-8 mx-auto text-blue-600 mb-2" />
                  <p className="text-2xl font-bold">{stats?.totalUsers || 0}</p>
                  <p className="text-sm text-muted-foreground">of {orgData.maxUsers || 5} Users</p>
                </div>
                <div className="text-center p-4 bg-muted/50 rounded-lg">
                  <UserCheck className="w-8 h-8 mx-auto text-cyan-600 mb-2" />
                  <p className="text-2xl font-bold">{stats?.totalEmployees || 0}</p>
                  <p className="text-sm text-muted-foreground">Employees</p>
                </div>
                <div className="text-center p-4 bg-muted/50 rounded-lg">
                  <Package className="w-8 h-8 mx-auto text-green-600 mb-2" />
                  <p className="text-2xl font-bold">{stats?.totalAssets || 0}</p>
                  <p className="text-sm text-muted-foreground">of {orgData.maxAssets || 100} Assets</p>
                </div>
                <div className="text-center p-4 bg-muted/50 rounded-lg">
                  <Ticket className="w-8 h-8 mx-auto text-rose-600 mb-2" />
                  <p className="text-2xl font-bold">{stats?.totalTickets || 0}</p>
                  <p className="text-sm text-muted-foreground">Tickets</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        </div>
      )}

      {/* Edit Details Modal */}
      {showDetailsEdit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="w-full max-w-lg mx-4">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold">Edit Organization Details</h2>
                <Button variant="ghost" size="icon" onClick={() => setShowDetailsEdit(false)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <form onSubmit={handleDetailsSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="name">Organization Name *</Label>
                  <Input
                    id="name"
                    value={detailsForm.name}
                    onChange={(e) => setDetailsForm({ ...detailsForm, name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="domain">Domain</Label>
                  <Input
                    id="domain"
                    value={detailsForm.domain}
                    onChange={(e) => setDetailsForm({ ...detailsForm, domain: e.target.value })}
                    placeholder="e.g., company.com"
                  />
                </div>
                <div>
                  <Label htmlFor="tagline">Tagline</Label>
                  <Input
                    id="tagline"
                    value={detailsForm.tagline}
                    onChange={(e) => setDetailsForm({ ...detailsForm, tagline: e.target.value })}
                    placeholder="Your organization's tagline"
                  />
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowDetailsEdit(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={updateMutation.isPending}>
                    {updateMutation.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Save Changes
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Edit Branding Modal */}
      {showBrandingEdit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 overflow-y-auto py-8">
          <Card className="w-full max-w-2xl mx-4 my-auto">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold">Edit Branding</h2>
                <Button variant="ghost" size="icon" onClick={() => setShowBrandingEdit(false)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <form onSubmit={handleBrandingSubmit} className="space-y-6 max-h-[70vh] overflow-y-auto pr-2">
                {/* Logos Section */}
                <div className="space-y-4">
                  <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">Logos</h3>
                  
                  {/* Primary Logo */}
                  <div>
                    <Label>Primary Logo</Label>
                    <div className="mt-2 flex items-center gap-4">
                      {brandingForm.logo ? (
                        <div className="relative">
                          <img 
                            src={brandingForm.logo} 
                            alt="Primary Logo" 
                            className="h-16 w-auto object-contain border rounded p-1"
                          />
                          <Button
                            type="button"
                            variant="destructive"
                            size="icon"
                            className="absolute -top-2 -right-2 h-6 w-6"
                            onClick={() => deleteFileMutation.mutate('logo')}
                            disabled={deleteFileMutation.isPending}
                          >
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                      ) : (
                        <div className="h-16 w-24 border-2 border-dashed rounded flex items-center justify-center text-muted-foreground">
                          <Image className="w-6 h-6" />
                        </div>
                      )}
                      <div className="flex-1">
                        <input
                          type="file"
                          id="logo-upload"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handleFileUpload(e, 'logo')}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => document.getElementById('logo-upload')?.click()}
                          disabled={uploadMutation.isPending}
                        >
                          {uploadMutation.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                          Upload Logo
                        </Button>
                        <p className="text-xs text-muted-foreground mt-1">PNG, JPG, SVG up to 5MB</p>
                      </div>
                    </div>
                  </div>

                  {/* Dark Mode Logo */}
                  <div>
                    <Label>Dark Mode Logo</Label>
                    <div className="mt-2 flex items-center gap-4">
                      {brandingForm.logoDark ? (
                        <div className="relative">
                          <div className="bg-gray-800 rounded p-1">
                            <img 
                              src={brandingForm.logoDark} 
                              alt="Dark Mode Logo" 
                              className="h-16 w-auto object-contain"
                            />
                          </div>
                          <Button
                            type="button"
                            variant="destructive"
                            size="icon"
                            className="absolute -top-2 -right-2 h-6 w-6"
                            onClick={() => deleteFileMutation.mutate('logoDark')}
                            disabled={deleteFileMutation.isPending}
                          >
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                      ) : (
                        <div className="h-16 w-24 border-2 border-dashed rounded bg-gray-800 flex items-center justify-center text-gray-500">
                          <Image className="w-6 h-6" />
                        </div>
                      )}
                      <div className="flex-1">
                        <input
                          type="file"
                          id="logoDark-upload"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handleFileUpload(e, 'logoDark')}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => document.getElementById('logoDark-upload')?.click()}
                          disabled={uploadMutation.isPending}
                        >
                          {uploadMutation.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                          Upload Dark Logo
                        </Button>
                        <p className="text-xs text-muted-foreground mt-1">Optional - for dark theme</p>
                      </div>
                    </div>
                  </div>

                  {/* Favicon */}
                  <div>
                    <Label>Favicon</Label>
                    <div className="mt-2 flex items-center gap-4">
                      {brandingForm.favicon ? (
                        <div className="relative">
                          <img 
                            src={brandingForm.favicon} 
                            alt="Favicon" 
                            className="h-8 w-8 object-contain border rounded p-1"
                          />
                          <Button
                            type="button"
                            variant="destructive"
                            size="icon"
                            className="absolute -top-2 -right-2 h-5 w-5"
                            onClick={() => deleteFileMutation.mutate('favicon')}
                            disabled={deleteFileMutation.isPending}
                          >
                            <X className="w-2 h-2" />
                          </Button>
                        </div>
                      ) : (
                        <div className="h-8 w-8 border-2 border-dashed rounded flex items-center justify-center text-muted-foreground">
                          <Image className="w-4 h-4" />
                        </div>
                      )}
                      <div className="flex-1">
                        <input
                          type="file"
                          id="favicon-upload"
                          accept="image/*,.ico"
                          className="hidden"
                          onChange={(e) => handleFileUpload(e, 'favicon')}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => document.getElementById('favicon-upload')?.click()}
                          disabled={uploadMutation.isPending}
                        >
                          {uploadMutation.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                          Upload Favicon
                        </Button>
                        <p className="text-xs text-muted-foreground mt-1">ICO, PNG 32x32</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Colors Section */}
                <div className="space-y-4">
                  <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">Brand Colors</h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="primaryColor">Primary Color</Label>
                      <div className="flex gap-2 mt-1">
                        <input
                          type="color"
                          value={brandingForm.primaryColor}
                          onChange={(e) => setBrandingForm({ ...brandingForm, primaryColor: e.target.value })}
                          className="w-10 h-10 rounded border cursor-pointer"
                        />
                        <Input
                          id="primaryColor"
                          value={brandingForm.primaryColor}
                          onChange={(e) => setBrandingForm({ ...brandingForm, primaryColor: e.target.value })}
                          className="font-mono flex-1"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="secondaryColor">Secondary Color</Label>
                      <div className="flex gap-2 mt-1">
                        <input
                          type="color"
                          value={brandingForm.secondaryColor}
                          onChange={(e) => setBrandingForm({ ...brandingForm, secondaryColor: e.target.value })}
                          className="w-10 h-10 rounded border cursor-pointer"
                        />
                        <Input
                          id="secondaryColor"
                          value={brandingForm.secondaryColor}
                          onChange={(e) => setBrandingForm({ ...brandingForm, secondaryColor: e.target.value })}
                          className="font-mono flex-1"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="accentColor">Accent Color</Label>
                      <div className="flex gap-2 mt-1">
                        <input
                          type="color"
                          value={brandingForm.accentColor}
                          onChange={(e) => setBrandingForm({ ...brandingForm, accentColor: e.target.value })}
                          className="w-10 h-10 rounded border cursor-pointer"
                        />
                        <Input
                          id="accentColor"
                          value={brandingForm.accentColor}
                          onChange={(e) => setBrandingForm({ ...brandingForm, accentColor: e.target.value })}
                          className="font-mono flex-1"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Letterhead Section */}
                <div className="space-y-4">
                  <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">Letterhead & PDF</h3>
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="letterheadEnabled"
                      checked={brandingForm.letterheadEnabled}
                      onChange={(e) => setBrandingForm({ ...brandingForm, letterheadEnabled: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <Label htmlFor="letterheadEnabled">Enable Letterhead</Label>
                  </div>
                  <div>
                    <Label>Letterhead Image</Label>
                    <div className="mt-2 flex items-center gap-4">
                      {brandingForm.letterheadImage ? (
                        <div className="relative">
                          <img 
                            src={brandingForm.letterheadImage} 
                            alt="Letterhead" 
                            className="h-12 w-auto object-contain border rounded p-1"
                          />
                          <Button
                            type="button"
                            variant="destructive"
                            size="icon"
                            className="absolute -top-2 -right-2 h-5 w-5"
                            onClick={() => deleteFileMutation.mutate('letterheadImage')}
                            disabled={deleteFileMutation.isPending}
                          >
                            <X className="w-2 h-2" />
                          </Button>
                        </div>
                      ) : (
                        <div className="h-12 w-32 border-2 border-dashed rounded flex items-center justify-center text-muted-foreground">
                          <FileText className="w-5 h-5" />
                        </div>
                      )}
                      <div className="flex-1">
                        <input
                          type="file"
                          id="letterheadImage-upload"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handleFileUpload(e, 'letterheadImage')}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => document.getElementById('letterheadImage-upload')?.click()}
                          disabled={uploadMutation.isPending}
                        >
                          <Upload className="w-4 h-4 mr-2" />
                          Upload
                        </Button>
                      </div>
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="footerText">Footer Text</Label>
                    <Input
                      id="footerText"
                      value={brandingForm.footerText}
                      onChange={(e) => setBrandingForm({ ...brandingForm, footerText: e.target.value })}
                      placeholder="Company footer text for PDFs"
                    />
                  </div>
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="watermarkEnabled"
                      checked={brandingForm.watermarkEnabled}
                      onChange={(e) => setBrandingForm({ ...brandingForm, watermarkEnabled: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <Label htmlFor="watermarkEnabled">Enable Watermark</Label>
                  </div>
                  {brandingForm.watermarkEnabled && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="watermarkText">Watermark Text</Label>
                        <Input
                          id="watermarkText"
                          value={brandingForm.watermarkText}
                          onChange={(e) => setBrandingForm({ ...brandingForm, watermarkText: e.target.value })}
                          placeholder="CONFIDENTIAL"
                        />
                      </div>
                      <div>
                        <Label htmlFor="watermarkOpacity">Opacity ({Math.round((brandingForm.watermarkOpacity || 0.1) * 100)}%)</Label>
                        <input
                          type="range"
                          id="watermarkOpacity"
                          min="0"
                          max="1"
                          step="0.05"
                          value={brandingForm.watermarkOpacity}
                          onChange={(e) => setBrandingForm({ ...brandingForm, watermarkOpacity: parseFloat(e.target.value) })}
                          className="w-full mt-2"
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Login Page Section */}
                <div className="space-y-4">
                  <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">Login Page</h3>
                  <div>
                    <Label htmlFor="loginWelcomeMessage">Welcome Message</Label>
                    <Input
                      id="loginWelcomeMessage"
                      value={brandingForm.loginWelcomeMessage}
                      onChange={(e) => setBrandingForm({ ...brandingForm, loginWelcomeMessage: e.target.value })}
                      placeholder="Welcome to IT Asset Management"
                    />
                  </div>
                  <div>
                    <Label>Login Background Image</Label>
                    <div className="mt-2 flex items-center gap-4">
                      {brandingForm.loginBackground ? (
                        <div className="relative">
                          <img 
                            src={brandingForm.loginBackground} 
                            alt="Login Background" 
                            className="h-16 w-28 object-cover border rounded"
                          />
                          <Button
                            type="button"
                            variant="destructive"
                            size="icon"
                            className="absolute -top-2 -right-2 h-5 w-5"
                            onClick={() => deleteFileMutation.mutate('loginBackground')}
                            disabled={deleteFileMutation.isPending}
                          >
                            <X className="w-2 h-2" />
                          </Button>
                        </div>
                      ) : (
                        <div className="h-16 w-28 border-2 border-dashed rounded flex items-center justify-center text-muted-foreground">
                          <Image className="w-5 h-5" />
                        </div>
                      )}
                      <div className="flex-1">
                        <input
                          type="file"
                          id="loginBackground-upload"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handleFileUpload(e, 'loginBackground')}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => document.getElementById('loginBackground-upload')?.click()}
                          disabled={uploadMutation.isPending}
                        >
                          <Upload className="w-4 h-4 mr-2" />
                          Upload Background
                        </Button>
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="loginOverlayColor">Overlay Color</Label>
                      <div className="flex gap-2 mt-1">
                        <input
                          type="color"
                          value={brandingForm.loginOverlayColor || '#000000'}
                          onChange={(e) => setBrandingForm({ ...brandingForm, loginOverlayColor: e.target.value })}
                          className="w-10 h-10 rounded border cursor-pointer"
                        />
                        <Input
                          id="loginOverlayColor"
                          value={brandingForm.loginOverlayColor}
                          onChange={(e) => setBrandingForm({ ...brandingForm, loginOverlayColor: e.target.value })}
                          className="font-mono flex-1"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="loginOverlayOpacity">Overlay Opacity ({Math.round((brandingForm.loginOverlayOpacity || 0.5) * 100)}%)</Label>
                      <input
                        type="range"
                        id="loginOverlayOpacity"
                        min="0"
                        max="1"
                        step="0.05"
                        value={brandingForm.loginOverlayOpacity}
                        onChange={(e) => setBrandingForm({ ...brandingForm, loginOverlayOpacity: parseFloat(e.target.value) })}
                        className="w-full mt-2"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-4 border-t">
                  <Button type="button" variant="outline" onClick={() => setShowBrandingEdit(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={updateMutation.isPending}>
                    {updateMutation.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Save Changes
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Edit Settings Modal */}
      {showSettingsEdit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="w-full max-w-lg mx-4">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold">Edit Settings</h2>
                <Button variant="ghost" size="icon" onClick={() => setShowSettingsEdit(false)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <form onSubmit={handleSettingsSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="timezone">Timezone</Label>
                  <select
                    id="timezone"
                    value={settingsForm.timezone}
                    onChange={(e) => setSettingsForm({ ...settingsForm, timezone: e.target.value })}
                    className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                  >
                    <option value="UTC">UTC</option>
                    <option value="Africa/Kampala">Africa/Kampala (EAT)</option>
                    <option value="Africa/Nairobi">Africa/Nairobi (EAT)</option>
                    <option value="Africa/Lagos">Africa/Lagos (WAT)</option>
                    <option value="Africa/Johannesburg">Africa/Johannesburg (SAST)</option>
                    <option value="Europe/London">Europe/London (GMT/BST)</option>
                    <option value="Europe/Paris">Europe/Paris (CET)</option>
                    <option value="America/New_York">America/New_York (EST)</option>
                    <option value="America/Los_Angeles">America/Los_Angeles (PST)</option>
                    <option value="Asia/Dubai">Asia/Dubai (GST)</option>
                    <option value="Asia/Singapore">Asia/Singapore (SGT)</option>
                  </select>
                </div>
                <div>
                  <Label htmlFor="dateFormat">Date Format</Label>
                  <select
                    id="dateFormat"
                    value={settingsForm.dateFormat}
                    onChange={(e) => setSettingsForm({ ...settingsForm, dateFormat: e.target.value })}
                    className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                  >
                    <option value="YYYY-MM-DD">YYYY-MM-DD (2026-02-04)</option>
                    <option value="DD/MM/YYYY">DD/MM/YYYY (04/02/2026)</option>
                    <option value="MM/DD/YYYY">MM/DD/YYYY (02/04/2026)</option>
                    <option value="DD-MM-YYYY">DD-MM-YYYY (04-02-2026)</option>
                    <option value="MMM DD, YYYY">MMM DD, YYYY (Feb 04, 2026)</option>
                    <option value="DD MMM YYYY">DD MMM YYYY (04 Feb 2026)</option>
                  </select>
                </div>
                <div>
                  <Label htmlFor="currency">Currency</Label>
                  <select
                    id="currency"
                    value={settingsForm.currency}
                    onChange={(e) => setSettingsForm({ ...settingsForm, currency: e.target.value })}
                    className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                  >
                    <option value="USD">USD - US Dollar</option>
                    <option value="UGX">UGX - Ugandan Shilling</option>
                    <option value="KES">KES - Kenyan Shilling</option>
                    <option value="TZS">TZS - Tanzanian Shilling</option>
                    <option value="EUR">EUR - Euro</option>
                    <option value="GBP">GBP - British Pound</option>
                    <option value="ZAR">ZAR - South African Rand</option>
                    <option value="NGN">NGN - Nigerian Naira</option>
                  </select>
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowSettingsEdit(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={updateMutation.isPending}>
                    {updateMutation.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Save Changes
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
